import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import '../assets/css/common.css';
import styles from '../assets/css/route-check.module.css';
import { registrationCheckService } from '../services/registrationCheckService';
import { routeService } from '../services/routeService';
import CustomButtons from '../components/CustomButtons';

function RouteCheck() {
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state || {};

  const getSessionStorageData = (key, fallback) => {
    try {
      const data = sessionStorage.getItem(key);
      return data ? JSON.parse(data) : fallback;
    } catch {
      return fallback;
    }
  };

  const [routes, setRoutes] = useState(
    state.routes && state.routes.length > 0
      ? state.routes
      : getSessionStorageData('routes', [{ routeName: '', fromStation: '', toStation: '', oneWayCost: '', commuterPassCost: '' }])
  );
  const [applicant, setApplicant] = useState(state.applicant ?? getSessionStorageData('applicant', ''));
  const [address, setAddress] = useState(state.address ?? getSessionStorageData('address', ''));
  const [busRequired, setBusRequired] = useState(state.busRequired ?? getSessionStorageData('busRequired', ''));
  const [walkingImpossible, setWalkingImpossible] = useState(state.walkingImpossible ?? getSessionStorageData('walkingImpossible', ''));
  const [totalCost, setTotalCost] = useState(state.totalCost ?? getSessionStorageData('totalCost', ''));
  const [attachment, setAttachment] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [commuteType, setCommuteType] = useState(state.commuteType ?? getSessionStorageData('commuteType', ''));

    const saveToSessionStorage = () => {
    sessionStorage.setItem('routes', JSON.stringify(routes));
    sessionStorage.setItem('applicant', JSON.stringify(applicant));
    sessionStorage.setItem('address', JSON.stringify(address));
    sessionStorage.setItem('busRequired', JSON.stringify(busRequired));
    sessionStorage.setItem('walkingImpossible', JSON.stringify(walkingImpossible));
    sessionStorage.setItem('totalCost', JSON.stringify(totalCost));
    sessionStorage.setItem('commuteType', JSON.stringify(commuteType));
  };

  useEffect(() => {
    const checkRegistrationStatus = async () => {
      try {
        const { 
          hasMemberData, 
          hasRouteData, 
          hasPhotoData, 
          hasConfidentialityData 
        } = await registrationCheckService.checkRegistration();
        
        if (hasConfidentialityData) {
          navigate("/");
        } else if (hasPhotoData) {
          navigate("/confidentiality-agreement");
        } else if (hasRouteData) {
          navigate("/photo-permission");
        } else if (!hasMemberData) {
          navigate("/member-registration");
        }
      } catch (error) {
        console.error('登録状態確認エラー:', error);
      }
    };
    
    checkRegistrationStatus();
  }, [navigate]);

  // 初回ロード時、未入力ならサーバから申請者/住所/経路の雛形を取得
  useEffect(() => {
    const preload = async () => {
      try {
        if (!applicant || !address) {
          const data = await routeService.getRegistration({});
          if (data) {
            setApplicant(data.applicant || '');
            setAddress(data.address || '');
            // flags: server returns 0/1 → 画面は 'はい'/'いいえ'
            if (data.busRequired !== undefined) setBusRequired(data.busRequired ? 'はい' : 'いいえ');
            if (data.walkingImpossible !== undefined) setWalkingImpossible(data.walkingImpossible ? 'はい' : 'いいえ');
            if (Array.isArray(data.routes) && data.routes.length) {
              setRoutes(data.routes.map(r => ({
                routeName: r.routeName || '',
                fromStation: r.fromStation || '',
                toStation: r.toStation || '',
                oneWayCost: r.oneWayCost ?? '',
                commuterPassCost: r.commuterPassCost ?? ''
              })));
            }
          }
        }
      } catch (e) {
        // 取得失敗は無視（手入力可能）
        console.warn('route preload failed', e);
      }
    };
    preload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleRouteChange = (index, field, value) => {
    const newRoutes = [...routes];
    newRoutes[index][field] = value;
    setRoutes(newRoutes);
  };

  const addRow = () => {
    setRoutes([...routes, { routeName: '', fromStation: '', toStation: '', oneWayCost: '', commuterPassCost: '' }]);
  };

  const removeRow = (index) => {
    if (routes.length === 1) return;
    const newRoutes = routes.filter((_, i) => i !== index);
    setRoutes(newRoutes);
  };

  const handleFileChange = (e) => {
  const file = e.target.files[0];
  if (!file) return;

  const reader = new FileReader();
  reader.onloadend = () => {
    setAttachment(reader.result);
  };
  reader.readAsDataURL(file);
};

  const hasTransport = commuteType === 'transport';

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!commuteType) {
      setErrorMessage('通勤手段を選択してください。');
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    // 交通機関利用ありの場合のみフルバリデーション
    if (hasTransport) {
      if (
        !busRequired ||
        !walkingImpossible ||
        !totalCost ||
        !attachment ||
        routes.some(route =>
          !route.routeName.trim() ||
          !route.fromStation.trim() ||
          !route.toStation.trim() ||
          !route.oneWayCost ||
          !route.commuterPassCost
        )
      ) {
        setErrorMessage('すべての項目を入力してください（添付ファイルも含みます）');
        window.scrollTo({ top: 0, behavior: 'smooth' });
        return;
      }
    }

    // OKなら確認画面へ
    saveToSessionStorage();
    setErrorMessage('');
    navigate('/route-check-confirm', {
      state: {
        routes: hasTransport ? routes : [],
        applicant,
        address,
        commuteType,
        busRequired: hasTransport ? busRequired : 'いいえ',
        walkingImpossible: hasTransport ? walkingImpossible : 'いいえ',
        totalCost: hasTransport ? totalCost : '0',
        attachmentURL: hasTransport ? attachment : null,
      },
    });
  };

  return (
    <div className={styles.container}>
      <div className="route-check-card">
        <h1 className={`${styles["text-primary"]} text-center mb-4`}>交通経路確認</h1>

        {/* エラーメッセージ表示 */}
        {errorMessage && (
          <div className="alert alert-danger" role="alert">
            {errorMessage}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          {/* 基本情報 */}
          <h2 className={styles["section-title"]}>基本情報</h2>
          <table className="kv-table">
            <tbody>
              <tr><th>申請者</th><td>{applicant}</td></tr>
              <tr><th>現住所</th><td>{address}</td></tr>
            </tbody>
          </table>

          {/* 通勤手段 */}
          <h2 className={`${styles["section-title"]}`}>通勤手段</h2>
          <div className="mb-3">
            <div className={styles["radio-group"]}>
              <div className="form-check">
                <input type="radio" name="commuteType" id="commute-transport" value="transport" checked={commuteType === 'transport'} onChange={(e) => setCommuteType(e.target.value)} required/>
                <label htmlFor="commute-transport" style={{cursor:'pointer',padding:'4px 8px'}}>交通機関を利用する</label>
              </div>
              <div className="form-check">
                <input type="radio" name="commuteType" id="commute-walk" value="walk" checked={commuteType === 'walk'} onChange={(e) => setCommuteType(e.target.value)}/>
                <label htmlFor="commute-walk" style={{cursor:'pointer',padding:'4px 8px'}}>徒歩のみ</label>
              </div>
              <div className="form-check">
                <input type="radio" name="commuteType" id="commute-remote" value="remote" checked={commuteType === 'remote'} onChange={(e) => setCommuteType(e.target.value)}/>
                <label htmlFor="commute-remote" style={{cursor:'pointer',padding:'4px 8px'}}>フルリモート（通勤なし）</label>
              </div>
            </div>
          </div>

          {/* 交通経路情報（交通機関利用時のみ） */}
          {hasTransport && (
          <>
          <h2 className={`${styles["section-title"]}`}>交通経路情報</h2>
          <table className="table">
            <thead>
              <tr>
                <th>交通機関名</th><th>発駅</th><th>着駅</th><th>片道料金（円）</th><th>定期代（円）</th><th>操作</th>
              </tr>
            </thead>
            <tbody>
              {routes.map((route, index) => (
                <tr key={index}>
                  <td><input type="text" className="form-control" placeholder="例: JR山手線" value={route.routeName} onChange={(e) => handleRouteChange(index, 'routeName', e.target.value)} required /></td>
                  <td><input type="text" className="form-control" placeholder="例: 新宿" value={route.fromStation} onChange={(e) => handleRouteChange(index, 'fromStation', e.target.value)} required /></td>
                  <td><input type="text" className="form-control" placeholder="例: 東京" value={route.toStation} onChange={(e) => handleRouteChange(index, 'toStation', e.target.value)} required /></td>
                  <td><input type="number" className="form-control" placeholder="例: 250" value={route.oneWayCost} onChange={(e) => handleRouteChange(index, 'oneWayCost', e.target.value)} required /></td>
                  <td><input type="number" className="form-control" placeholder="例: 10000" value={route.commuterPassCost} onChange={(e) => handleRouteChange(index, 'commuterPassCost', e.target.value)} required /></td>
                  <td>
                    {routes.length > 1 && (
                      <button type="button" className="btn btn-danger btn-sm" onClick={() => removeRow(index)}>削除</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <button type="button" className="btn btn-secondary btn-sm mb-3" onClick={addRow}>行を追加</button>

          {/* 利用状況 */}
          <h2 className={`${styles["section-title"]}`}>利用状況</h2>
          <div className="mb-3">
            <label className="form-label">自宅から最寄駅までバスを利用しますか？</label>
            <div className={styles["radio-group"]}>
              <div className="form-check">
                <input type="radio" name="bus" id="bus-yes" value="はい" checked={busRequired === 'はい'} onChange={(e) => setBusRequired(e.target.value)} required/>
                <label htmlFor="bus-yes" style={{cursor:'pointer',padding:'4px 8px'}}>はい</label>
              </div>
              <div className="form-check">
                <input type="radio" name="bus" id="bus-no" value="いいえ" checked={busRequired === 'いいえ'} onChange={(e) => setBusRequired(e.target.value)}/>
                <label htmlFor="bus-no" style={{cursor:'pointer',padding:'4px 8px'}}>いいえ</label>
              </div>
            </div>
          </div>
          <div className="mb-3">
            <label className="form-label">徒歩が不可能な距離（直線距離2km以上）ですか？</label>
            <div className={styles["radio-group"]}>
              <div className="form-check">
                <input type="radio" name="walk" id="walk-yes" value="はい" checked={walkingImpossible === 'はい'} onChange={(e) => setWalkingImpossible(e.target.value)} required/>
                <label htmlFor="walk-yes" style={{cursor:'pointer',padding:'4px 8px'}}>はい</label>
              </div>
              <div className="form-check">
                <input type="radio" name="walk" id="walk-no" value="いいえ" checked={walkingImpossible === 'いいえ'} onChange={(e) => setWalkingImpossible(e.target.value)}/>
                <label htmlFor="walk-no" style={{cursor:'pointer',padding:'4px 8px'}}>いいえ</label>
              </div>
            </div>
          </div>

          {/* 交通費総額 */}
          <div className="mb-3">
            <label className="form-label">交通費総額（円）</label>
            <input type="number" className="form-control" placeholder="例: 20000" value={totalCost} onChange={(e) => setTotalCost(e.target.value)} required />
          </div>

          {/* 添付ファイル */}
          <h2 className={`${styles["section-title"]}`}>現住所から最寄駅までの経路情報</h2>
          <div className="mb-3">
            <label className="form-label">添付ファイル（地図や経路） <span style={{color:'red'}}>*</span></label>
            <small className="text-muted d-block mb-1">JPEG/PNG形式、10MB以下の画像を選択してください</small>
            <input type="file" accept="image/jpeg,image/png,image/gif,image/webp" onChange={handleFileChange} className="form-control" />
          </div>

          </>
          )}

          {/* 通勤なしの場合のメッセージ */}
          {commuteType && !hasTransport && (
            <div className="alert alert-info" style={{fontSize:'0.9rem'}}>
              {commuteType === 'walk' ? '徒歩通勤のため、交通経路の入力は不要です。' : 'フルリモートのため、交通経路の入力は不要です。'}
            </div>
          )}

          {/* 送信ボタン */}
          <div className="d-grid mt-4">
            <CustomButtons
              rightLabel="確認画面へ"
              rightType="submit"
              onRightClick={handleSubmit}
              leftLabel=""
              leftClassName="d-none"
            />
          </div>
        </form>


      </div>
    </div>
  );
}

export default RouteCheck;
